// ペンストロークの入力中プレビュー用 Canvas2D レイヤ。
// - 入力中のストロークは use-pointer-mode から rAF 増分描画される
// - 確定後は通常オブジェクトとして保存・描画し、このレイヤは消去する
// - 親（CanvasStage）から canvas DOM へアクセスできるよう ref で実体を取得する
//
// React 19 では関数コンポーネントが直接 ref prop を受け取れるため、forwardRef は不要。

import { useEffect, useImperativeHandle, useRef, type Ref } from "react";
import { resolvePenPreviewRect } from "../../lib/pen-preview-rect";
import { useCanvasRect } from "./use-canvas-rect";

export interface PenLayerHandle {
  readonly canvas: HTMLCanvasElement;
}

interface Props {
  readonly handleRef?: Ref<PenLayerHandle | null> | undefined;
}

export function CanvasPenLayer({ handleRef }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // 入力中プレビューの Canvas を「固定ステージ」と「現在のキャンバス矩形」の和集合へ追従させる。
  // キャンバス外にも配置可という契約に合わせ、小 / 中サイズでキャンバス矩形の外側（ただしステージ内）
  // から描き始めたストロークも、大サイズでキャンバス矩形がステージ外へ張り出した領域も、確定前の
  // プレビュー段階から可視化する（use-pointer-mode 側で描画座標からこの矩形の原点を差し引く）。
  const previewRect = resolvePenPreviewRect(useCanvasRect());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = previewRect.w;
    canvas.height = previewRect.h;
  }, [previewRect.w, previewRect.h]);

  useImperativeHandle<PenLayerHandle | null, PenLayerHandle | null>(
    handleRef,
    () => {
      const canvas = canvasRef.current;
      return canvas ? ({ canvas } satisfies PenLayerHandle) : null;
    },
    []
  );

  return (
    <canvas
      ref={canvasRef}
      data-testid="canvas-pen-layer"
      style={{
        position: "absolute",
        left: `${previewRect.x}px`,
        top: `${previewRect.y}px`,
        width: `${previewRect.w}px`,
        height: `${previewRect.h}px`,
        pointerEvents: "none",
      }}
    />
  );
}
